import React, { useMemo } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { useAdminUsers, type AdminUserOption } from '../hooks/useAdminUsers';
import { sharedAccountManagers } from '../config/shared-account-managers';

export interface AccountManagerSelection {
  name: string;
  email: string;
}

interface AccountManagerSelectProps {
  /** Unique id for the label/control pair (create vs edit dialogs differ). */
  labelId: string;
  /** The org's current accountManagerEmail — the option's identity. */
  valueEmail: string;
  /** The org's current accountManagerName, used to label a legacy value. */
  valueName?: string;
  /** null clears both fields (unassigned). */
  onChange: (selection: AccountManagerSelection | null) => void;
  disabled?: boolean;
  helperText?: string;
}

/** Emails are compared case-insensitively — Cognito and the org table disagree on case. */
function sameEmail(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Shared "Account Manager" dropdown for the org create dialog and the org
 * detail page's edit dialog.
 *
 * Replaces the two free-text boxes that produced 25 spellings of 9 people in
 * prod. Options are the people in the `Admins` Cognito group — our staff —
 * plus the stack's role mailboxes from `config/shared-account-managers`,
 * because the account manager is the contact on OUR side who looks after the
 * client, and that contact is often a shared inbox rather than a person. The
 * org's owner/payer is a separate field and is not touched here.
 *
 * A value that predates this picker is preserved as a disabled-looking "legacy"
 * option rather than silently blanked: opening the dialog on one of the ~105
 * legacy orgs and pressing Save must not wipe the address the run-limit
 * notifier emails.
 */
const AccountManagerSelect: React.FC<AccountManagerSelectProps> = ({
  labelId,
  valueEmail,
  valueName,
  onChange,
  disabled,
  helperText,
}) => {
  const { admins, loading, error } = useAdminUsers();

  const current = valueEmail?.trim() || '';

  // The individuals in the Admins group PLUS this stack's role mailboxes,
  // deduped by email with the Cognito record winning — a shared inbox that also
  // has a login (admin@brokerai.co.za does) is then listed once, under the name
  // Cognito holds. Sorted as one list so the two sources are not visibly
  // different things to choose between; they are both "our contact".
  const selectable = useMemo<AdminUserOption[]>(() => {
    const merged = [...admins];
    for (const shared of sharedAccountManagers()) {
      if (merged.some((option) => sameEmail(option.email, shared.email))) continue;
      merged.push({
        userId: `shared:${shared.email}`,
        email: shared.email,
        name: shared.name,
      });
    }
    return merged.sort((a, b) => a.name.localeCompare(b.name));
  }, [admins]);

  // A stored value that is neither an admin user nor a role mailbox (legacy free
  // text, a departed colleague) still has to be selectable, or the Select
  // renders blank and the first Save silently clears it.
  const legacyOption: AdminUserOption | null = useMemo(() => {
    if (!current) return null;
    if (selectable.some((option) => sameEmail(option.email, current))) return null;
    return {
      userId: `legacy:${current}`,
      email: current,
      name: valueName?.trim() || current,
    };
  }, [selectable, current, valueName]);

  const options = useMemo(
    () => (legacyOption ? [...selectable, legacyOption] : selectable),
    [selectable, legacyOption],
  );

  const handleChange = (selectedEmail: string) => {
    if (!selectedEmail) {
      onChange(null);
      return;
    }
    const match = options.find((o) => sameEmail(o.email, selectedEmail));
    if (!match) return;
    onChange({ name: match.name, email: match.email });
  };

  // `options` always contains `current` when it is set — as a real admin, a role
  // mailbox, or the legacy entry above — including while the list is still
  // loading (admins is [] then, so legacyOption is built). So the value is never
  // out-of-range and needs no guard.
  const selectValue = current;

  return (
    // Disabled on error as well as while loading: when the lookup fails,
    // `admins` is [] and the only options left are the role mailboxes, the
    // legacy entry and "Unassigned" — so an interactive Select would let someone
    // replace or clear a live manager address (the one the run-limit notifier
    // emails) on the strength of a failed fetch. The role mailboxes are known
    // without the fetch, but they are not a reason to reopen the control: it
    // stays read-only until we know who the admins are.
    <FormControl fullWidth disabled={disabled || loading || !!error}>
      <InputLabel id={labelId}>Account Manager</InputLabel>
      <Select
        labelId={labelId}
        label="Account Manager"
        value={selectValue}
        onChange={(e) => handleChange(String(e.target.value))}
      >
        <MenuItem value="">
          <em>Unassigned</em>
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.userId} value={option.email}>
            {option.name} — {option.email}
            {/* Suppressed while loading: `admins` is [] until the fetch lands, so
                EVERY stored value looks legacy for that moment — including a
                real admin's. */}
            {!loading && legacyOption && option.userId === legacyOption.userId
              ? ' (not an admin user)'
              : ''}
          </MenuItem>
        ))}
      </Select>
      {(helperText || error) && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, ml: 0.5 }}
        >
          {error || helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default AccountManagerSelect;
